// src/pages/healthcare/intray/InTrayPage.tsx

import React, { useEffect, useState, useCallback } from "react";
import { Space, Tooltip, Button, Typography, Segmented, Popconfirm } from "antd";
import { ReloadOutlined, FileTextOutlined, UserOutlined, CheckOutlined, PrinterOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { format, formatDistanceToNow } from "date-fns";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
	fetchTestsCompany,
	markTestAsReviewed,
	TableFilterSort,
} from "../../../api/healthcare/Testing/TestApi";

import Layout from "../../../components/Layout";
import PageContainer from "../../../components/shared/PageContainer";
import PageToolbar from "../../../components/shared/PageToolbar";
import DataTable from "../../../components/shared/DataTable";
import StatusTag from "../../../components/shared/StatusTag";
import TestResultsTab from "../results/testOrderComponents/TestResultsTab";
import CreateTestModal from "../results/CreateTestOrderModal";
import PrintOrderModal from "../results/PrintOrderModal";
import EditPatientModal from "../patient/PatientModal";

import { Test } from "../../../types/healthcare/TestTypes";
import { Patient } from "../../../types/healthcare/PatientTypes";
import { useNotifications } from "../../../context/NotificationContext";
import { useXoeTestOrderFocus } from "../../../hooks/xoe/useXoeTestOrderFocus";
import { loadUserPreferences, saveUserPreferences } from "../../../utils/userPrefs/UserPrefs";

const { Text } = Typography;

/**
 * "mine" sends the literal `ordering_provider: ["me"]` — the backend resolves the
 * practitioner from the token and ignores any caller-supplied id.
 * "all" stays available on purpose: an order placed by reception carries an
 * `ordering_provider_id` that matches no logged-in practitioner, so under "mine"
 * alone those results would be invisible to everyone.
 */
type InTrayScope = "mine" | "all";

const INTRAY_QUERY_KEY = "intray-orders";

const InTrayPage: React.FC = () => {
	const queryClient = useQueryClient();
	const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

	const [isTestModalOpen, setIsTestModalOpen] = useState<boolean>(false);
	const [currentTest, setCurrentTest] = useState<Partial<Test | null>>(null);

	// In Tray was the one surface that lists test orders with no print action at all.
	const [printOrderId, setPrintOrderId] = useState<number | null>(null);

	const [isPatientModalOpen, setIsPatientModalOpen] = useState<boolean>(false);
	const [currentPatient, setCurrentPatient] = useState<Patient | null>(null);

	const { showError, showSuccess } = useNotifications();

	const [pageNumber, setPageNumber] = useState<number>(1);
	const [searchQuery, setSearchQuery] = useState<string>("");
	// Default to the results-age sort so the FIRST request carries it — the backend
	// otherwise falls through to created_at DESC, which is order age, not results age.
	const [sortField, setSortField] = useState<string | undefined>("results_updated_at");
	const [sortOrder, setSortOrder] = useState<"ascend" | "descend" | undefined>("descend");
	const [scope, setScope] = useState<InTrayScope>(
		loadUserPreferences().intrayScope === "all" ? "all" : "mine",
	);

	const RESULTS_PER_PAGE = loadUserPreferences().tableRowsPerPage;

	const tableParams: TableFilterSort = {
		filters: {
			needs_review: ["1"],
			...(scope === "mine" ? { ordering_provider: ["me"] } : {}),
			...(searchQuery ? { patient_name: [searchQuery] } : {}),
		},
		...(sortField && sortOrder ? { sorter: { field: sortField, order: sortOrder } } : {}),
	};

	const { data, isLoading, isFetching, isError } = useQuery({
		queryKey: [INTRAY_QUERY_KEY, pageNumber, searchQuery, sortField, sortOrder, scope],
		queryFn: () => fetchTestsCompany(pageNumber, RESULTS_PER_PAGE, undefined, tableParams),
	});

	useEffect(() => {
		if (isError) showError("Failed to load results. Please try again later.");
	}, [isError, showError]);

	const orders = data?.data ?? [];
	const totalResults = data?.total ?? 0;

	const reloadOrders = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: [INTRAY_QUERY_KEY] });
	}, [queryClient]);

	// One click clears an order that would otherwise take four interactions and a form
	// (expand row → expand result → change a Select → Save & Update, once per result).
	// The endpoint marks every available result on the order reviewed in one transaction
	// and is idempotent, so a double-click is harmless. Invalidate-and-refetch only —
	// no optimistic write, so the row only disappears once the server agrees it has.
	const markReviewed = useMutation({
		mutationFn: (id: number) => markTestAsReviewed(id),
		onSuccess: () => {
			showSuccess("Results marked as reviewed");
			reloadOrders();
		},
		onError: () => showError("Failed to mark results as reviewed"),
	});

	// Same as ResultsPage: an open test order binds Xoe to its patient (86d3z0ty3 / X1). This
	// page opens the very same `CreateTestModal` over the same rows, so leaving it out here
	// would make the assistant follow the patient on one screen and not the one beside it.
	useXoeTestOrderFocus(isTestModalOpen, currentTest);

	const handleViewTest = useCallback((test: Test) => {
		setCurrentTest(test);
		setIsTestModalOpen(true);
	}, []);

	const handleTestModalClose = useCallback(() => {
		setIsTestModalOpen(false);
		setCurrentTest(null);
	}, []);

	const handleOpenPatient = (patient: Patient) => {
		setCurrentPatient(patient);
		setIsPatientModalOpen(true);
	};

	const handleScopeChange = (next: InTrayScope) => {
		setScope(next);
		// Page 3 of "All" is very often an empty page of "Mine".
		setPageNumber(1);
		saveUserPreferences({ intrayScope: next });
	};

	const handleReset = () => {
		setSearchQuery("");
		setSortField("results_updated_at");
		setSortOrder("descend");
		handleScopeChange("mine");
	};

	const columns: ColumnsType<Test> = [
		{
			title: "Patient",
			key: "patient_name",
			fixed: "left",
			width: 180,
			render: (_, record) =>
				record.patient ? (
					<span style={{ fontWeight: 500 }}>
						{record.patient.first_name} {record.patient.last_name}
					</span>
				) : (
					<Text type="secondary">Unknown</Text>
				),
		},
		{
			title: "Sample ID",
			dataIndex: "sample_id",
			key: "sample_id",
			width: 140,
			sorter: true,
			render: (text) => text || "—",
		},
		{
			title: "Test Package",
			key: "test_package",
			width: 220,
			render: (_, record) =>
				record.custom_test_packages?.length
					? record.custom_test_packages.map((p) => p.name).join(", ")
					: <Text type="secondary">—</Text>,
		},
		{
			title: "Order Status",
			dataIndex: "status",
			key: "status",
			width: 160,
			sorter: true,
			render: (status: string) => <StatusTag status={status} label={status?.replace(/_/g, " ")} />,
		},
		{
			// `dataIndex` is load-bearing, not decoration: Ant Design derives
			// SorterResult.field from it and DataTable's onSort bridge is gated on
			// `if (s.field)`. Without it the header arrow moves and nothing refetches.
			title: "Results Updated",
			dataIndex: "results_updated_at",
			key: "results_updated_at",
			width: 190,
			sorter: true,
			// Newest-first is the default, so the cycle has to START at descend —
			// with antd's default ["ascend","descend"] the first click on an already
			// descending column CLEARS the sort, which the onSort bridge drops.
			sortDirections: ["descend", "ascend"],
			render: (value?: string) => {
				if (!value) return "—";
				try {
					const d = new Date(value);
					return (
						<div>
							<div>
								{format(d, "dd/MM/yyyy")} {format(d, "HH:mm")}
							</div>
							<div style={{ fontSize: 12, color: "var(--mg-text-secondary)" }}>
								{formatDistanceToNow(d, { addSuffix: true })}
							</div>
						</div>
					);
				} catch {
					return value;
				}
			},
		},
		{
			title: "Created",
			dataIndex: "created_at",
			key: "created_at",
			width: 130,
			sorter: true,
			render: (date) => {
				if (!date) return "—";
				try {
					return format(new Date(date), "dd/MM/yyyy");
				} catch {
					return date.toString();
				}
			},
		},
		{
			title: "Actions",
			key: "actions",
			width: 140,
			align: "center",
			fixed: "right",
			render: (_, record) => (
				<Space size={4}>
					<Popconfirm
						title="Mark results reviewed?"
						description="All available results for this test order will be marked as reviewed."
						okText="Mark reviewed"
						cancelText="Cancel"
						onConfirm={() => markReviewed.mutate(record.id)}
					>
						<Tooltip title="Mark results reviewed">
							<Button
								size="small"
								type="primary"
								icon={<CheckOutlined />}
								loading={markReviewed.isPending && markReviewed.variables === record.id}
								data-testid="intray-mark-reviewed"
								// Every handler in this table needs this — DataTable makes the whole
								// row interactive, so a bare click would also expand the row.
								onClick={(e) => e.stopPropagation()}
							/>
						</Tooltip>
					</Popconfirm>
					<Tooltip title="Print request slip">
						<Button
							size="small"
							icon={<PrinterOutlined />}
							data-testid="intray-print-order"
							onClick={(e) => {
								e.stopPropagation();
								setPrintOrderId(record.id);
							}}
						/>
					</Tooltip>
					<Tooltip title="View Test Order">
						<Button
							size="small"
							icon={<FileTextOutlined />}
							data-testid="intray-view-order"
							onClick={(e) => {
								e.stopPropagation();
								handleViewTest(record);
							}}
						/>
					</Tooltip>
					{record.patient && (
						<Tooltip title="View Patient">
							<Button
								size="small"
								icon={<UserOutlined />}
								data-testid="intray-view-patient"
								onClick={(e) => {
									e.stopPropagation();
									handleOpenPatient(record.patient!);
								}}
							/>
						</Tooltip>
					)}
				</Space>
			),
		},
	];

	return (
		<Layout>
			<PageContainer data-testid="intray-root">
				<PageToolbar
					extraActions={[
						{
							label: "Refresh",
							icon: <ReloadOutlined spin={isFetching} />,
							onClick: reloadOrders,
							type: "default",
							disabled: isFetching,
							testId: "intray-refresh",
						},
					]}
					filters={[
						{
							type: "custom",
							key: "scope",
							render: (
								<Segmented
									data-testid="intray-scope"
									value={scope}
									onChange={(value) => handleScopeChange(value as InTrayScope)}
									options={[
										{ label: "Mine", value: "mine" },
										{ label: "All", value: "all" },
									]}
								/>
							),
						},
						{
							type: "search",
							placeholder: "Search by patient…",
							testId: "intray-search",
							value: searchQuery,
							onChange: (v) => {
								setSearchQuery(v);
								setPageNumber(1);
							},
							width: 240,
						},
					]}
					onReset={handleReset}
				/>

				<DataTable
					columns={columns}
					dataSource={orders}
					rowKey="id"
					loading={isLoading}
					data-testid="intray-table"
					expandable={{
						expandedRowKeys,
						onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
						rowExpandable: () => true,
						expandedRowRender: (record) => (
							<TestResultsTab currentOrder={record} patient={record.patient} onResultSaved={reloadOrders} />
						),
					}}
					total={totalResults}
					currentPage={pageNumber}
					pageSize={RESULTS_PER_PAGE}
					onPageChange={(page) => setPageNumber(page)}
					sortField={sortField}
					sortOrder={sortOrder}
					onSort={(field, order) => {
						setSortField(field);
						setSortOrder(order);
					}}
					emptyTitle="No Pending Results"
					emptyDescription={
						scope === "mine" ? "No results awaiting your review." : "No results awaiting review."
					}
					scrollX={1400}
					fillHeight
				/>

				{isTestModalOpen && currentTest && (
					<CreateTestModal
						isOpen={isTestModalOpen}
						onClose={handleTestModalClose}
						onSave={async () => {
							reloadOrders();
							handleTestModalClose();
						}}
						currentOrder={currentTest}
						setCurrentOrder={setCurrentTest}
						patient={currentTest.patient}
					/>
				)}

				{printOrderId !== null && (
					<PrintOrderModal
						isOpen={printOrderId !== null}
						onClose={() => setPrintOrderId(null)}
						orderIds={[printOrderId]}
						title={`Print Test Order #${printOrderId}`}
					/>
				)}

				{isPatientModalOpen && currentPatient && (
					<EditPatientModal
						isOpen={isPatientModalOpen}
						onClose={() => {
							setIsPatientModalOpen(false);
							setCurrentPatient(null);
						}}
						patient={currentPatient}
					/>
				)}
			</PageContainer>
		</Layout>
	);
};

export default InTrayPage;
